import {
    createContext,
    createElement,
    type ReactNode,
    useContext,
    useSyncExternalStore,
} from 'react';
import { z } from 'zod';

import { type Mode } from '@libraries/typescript/api_client';

const STORAGE_KEY = 'phoebe-mode';

const modeSchema = z.enum(['live', 'sandbox']);
const ModeOverrideContext = createContext<Mode | null>(null);

function parseMode(value: unknown): Mode {
    const result = modeSchema.safeParse(value);
    return result.success ? result.data : 'live';
}

let currentMode: Mode | null = null;
let channel: BroadcastChannel | null = null;
let storageHandler: ((event: StorageEvent) => void) | null = null;
const listeners = new Set<() => void>();

function readStoredMode(): Mode {
    let storedMode: string | null = null;
    try {
        storedMode = localStorage.getItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing, etc.)
    }

    return parseMode(storedMode);
}

function ensureCurrentMode(): Mode {
    if (currentMode === null) {
        currentMode = readStoredMode();
    }

    return currentMode;
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

export const modeStore = {
    getMode(): Mode | undefined {
        return ensureCurrentMode();
    },
    setMode(mode: Mode) {
        const previousMode = ensureCurrentMode();
        if (mode === previousMode) {
            return;
        }

        currentMode = mode;
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // localStorage unavailable (private browsing, quota exceeded, etc.)
        }
        channel?.postMessage(mode);
        emitChange();
    },
    activate() {
        ensureCurrentMode();

        if (channel !== null) {
            return;
        }

        channel = new BroadcastChannel('phoebe-mode');
        channel.onmessage = (event) => {
            const newMode = parseMode(event.data);
            if (newMode !== currentMode) {
                currentMode = newMode;
                emitChange();
            }
        };

        storageHandler = (event) => {
            if (event.key === STORAGE_KEY && event.newValue !== null) {
                const newMode = parseMode(event.newValue);
                if (newMode !== currentMode) {
                    currentMode = newMode;
                    emitChange();
                }
            }
        };
        window.addEventListener('storage', storageHandler);
    },
    deactivate() {
        currentMode = null;

        if (channel) {
            channel.close();
            channel = null;
        }

        if (storageHandler) {
            window.removeEventListener('storage', storageHandler);
            storageHandler = null;
        }
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function ModeOverrideProvider({
    mode,
    children,
}: {
    readonly mode: Mode;
    readonly children?: ReactNode;
}) {
    return createElement(
        ModeOverrideContext.Provider,
        { value: mode },
        children,
    );
}

export function useMode(): Mode | undefined {
    const overriddenMode = useContext(ModeOverrideContext);
    const storedMode = useSyncExternalStore(
        modeStore.subscribe,
        modeStore.getMode,
    );

    return overriddenMode ?? storedMode;
}
